import { _t } from '@/locale';
import { classnames, isSistaminuten } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import { modalActions } from '@/actions/modalActions';
import { FormState } from '@/reducers/formReducer';
import useMobileView from '@/hooks/useMobileView';

import Avatar from '@/components/elements/Avatar';
import Label from '@/components/elements/Label/Label';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import Card from '@/components/elements/Card/Card';
import { isBookableNormalizedPlace } from '@/helpers';
import { Button } from '@/components/elements/forms/buttons';

import { useSelector } from 'react-redux';
import { NormalizedPlace } from '@/types/place';
import { NormalizedEmployee } from '@/types/employee';
import { ReviewsState } from '@/reducers/reviewsReducer';
import { TIME_FILTER } from '@/constants/timeConstants';

type EmployeeCardProps = {
  employeeId: number;
  placeId: number;
  reviewsUid: string;
  handleOnPickEmployee: (employee: NormalizedEmployee) => void;
};

/**
 * Legacy employee card on the place page, rendered when reviews-v2 is enabled and listcards-v2 is disabled.
 * Delete once listcards-v2 is fully rolled out.
 */
export const LegacyEmployeeListCardV2 = ({
  handleOnPickEmployee,
  employeeId,
  placeId,
  reviewsUid,
}: EmployeeCardProps) => {
  const reduxDispatch = useAppDispatch();

  const { isMobileView } = useMobileView();

  const employee = useSelector(
    (state: { employees: { all: Record<number, NormalizedEmployee> } }) => state.employees.all[employeeId],
  );
  const employeeReviews = useSelector(
    (state: { reviews: ReviewsState }) => state.reviews?.byUid?.[reviewsUid]?.details?.employeeStats?.[employeeId],
  );

  const place = useSelector((state: { place: Record<number, NormalizedPlace> }) => state.place[placeId]);

  const timeFilterFromState = useSelector(
    (state: { form: FormState }) => state.form[reviewsUid]?.searchFilter?.timeFilter,
  );

  if (!employee?.about || employee.about.inactive) {
    return null;
  }

  const { name, jobTitle, settings, certificates, avatar } = employee.about || {};

  const employeeJobTilte = (jobTitle || '').split('.').join('. ');

  const hasCertificate = certificates?.length || false;
  const acceptsGiftcard = !isSistaminuten() && settings?.acceptsGiftCard;
  const hasBadges = acceptsGiftcard || hasCertificate;

  const showBookButton = isBookableNormalizedPlace({ employee, place });

  const onShowReviewModal = () => {
    reduxDispatch(
      modalActions.reviews({
        show: true,
        placeId,
        employeeId,
        serviceId: undefined,
        timeFilter: timeFilterFromState || TIME_FILTER.FOREVER,
        focus: 'employee',
        context: 'placePage',
      }),
    );
  };

  return (
    <Card size="sm" childClassName="@container">
      <div className={classnames('gap-sm @sm:flex-row flex flex-col')}>
        <div
          role={'button'}
          onClick={onShowReviewModal}
          className="flex flex-1 cursor-pointer items-center justify-between text-start outline-none">
          <div className="gap-sm flex flex-1 items-start overflow-hidden">
            <div className="gap-xs flex w-full flex-col">
              <div className="flex justify-between">
                <div className="gap-sm flex w-full">
                  <Avatar src={settings?.showPhoto === false ? null : avatar} size={'md'} />
                  <div className="gap-xs flex w-full flex-col justify-center overflow-hidden">
                    <div className="gap-sm flex w-full items-center justify-between">
                      <div className="gap-2xs flex flex-col">
                        <h4 className="text-l text-fg_primary">{name}</h4>
                        <div className="text-s gap-2xs flex items-start">
                          <p className={`text-fg_secondary flex w-full items-start`}>
                            {Boolean(employeeJobTilte) && employeeJobTilte}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {hasBadges && (
                <div className="space-x-sm @sm:hidden flex items-center">
                  {hasCertificate && (
                    <a
                      href="#certificates"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}>
                      <Label variant="certificate" />
                    </a>
                  )}
                  {acceptsGiftcard && <Label variant="giftcard" />}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`border-border_regular @sm:border-l @sm:border-t-0 min-w-[190px] border-t`}>
          <div
            className={classnames('text-s flex h-full w-full items-center', 'pt-sm @sm:pt-0 @sm:pl-sm @sm:py-xs', {
              'justify-end': !isMobileView,
              'justify-between': isMobileView,
            })}>
            <RatingV2
              ratingCount={employeeReviews?.count ?? 0}
              averageRating={employeeReviews?.score ?? 0}
              size={'md'}
              className={'mr-sm'}
              direction={isMobileView ? 'row' : 'column'}
              onReviewsClick={
                employeeReviews?.count
                  ? () => {
                      onShowReviewModal();
                    }
                  : undefined
              }
              withRatingCounter
              withAverageScore={Boolean(employeeReviews?.score)}
              withMultipleStars={false}
            />

            <div className={'min-w-[56px]'}>
              {showBookButton && handleOnPickEmployee ? (
                <Button
                  label={_t('book')}
                  onClick={() => {
                    handleOnPickEmployee(employee);
                  }}
                  size={'sm'}
                  variant={'primary'}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LegacyEmployeeListCardV2;
